import api from '../../../../../api';
import { ResponseSuccess } from '../../../../../shared/types';
import {
  ApplyCouponCodeRequest,
  GetInvoiceURLRequest,
  PurchaseEmailVerificationCreditsRequest,
  ModifySubscriptionRequest,
  PurchaseSubscriptionRequest,
  PurchaseModifySubscriptionRequest,
  ReactivateResumeSubscriptionRequest,
  HandleSubscriptionRequest,
  CalculatePayRequest,
  UpgradePlanRequest,
  DowngradePlanRequest,
} from '../types';
import {
  CalculateAiCreditsPricingsRequest,
  PurchaseAiCreditsRequest,
} from '../types/ai-credits';
import {
  LTDModifyPayload,
  LTDPurchasePayload,
  LTDUpgradePayload,
} from '../types/ltd-plans';
import {
  CalculateCreditsPricesRequest,
  CreditsTransactionStatusRequest,
  PurchaseCreditsRequest,
} from '../types/purchase-credits';
import {
  ExportCreditHistoryPayload,
  GetOrderDetailsByIdPayload,
} from '../types/purchase-history';
import {
  DynamicCustomPlansRequest,
  PurchaseLeadFinderCreditsRequest,
} from '../types/purchase-lead-finder-credits';
import type {
  PaddlePreviewSubscriptionRequest,
  PaddleUpgradeSubscriptionRequest,
  PaddleTransactionPollingRequest,
  PaddleTransactionPollingResponse,
  PaddleDynamicPlanRequest,
  PaddleValidateCouponRequest,
  PaddleValidateCouponResponse,
  PaddleCustomerPortalRequest,
  PaddleCustomerPortalResponse,
  PaddlePauseResumeRequest,
  PaddleApplicationType,
  PaddlePreviewSubscriptionResponse,
  PaddleSubscriptionMetadataItem,
  PaddleRetainEventRequest,
} from '../types/paddle';

export const getAccountSubscription = () =>
  api.get<ResponseSuccess>('/subscription');

export const getAccountSubscriptionPlanDetail = (id: string) =>
  api.get<ResponseSuccess>(`retrive-upcoming-invoice?customerId=${id}`);

export const getInboxAnalyzerSubscriptionPlanDetail = (id: string) =>
  api.get<ResponseSuccess>(
    `/inbox-analyzer/subscription/retrieve-upcoming-invoice?customerId=${id}`,
  );

export const getLeadFinderSubscription = () =>
  api.get<ResponseSuccess>('/lead-finder/subscription');

export const getInboxAnalyzerSubscription = () =>
  api.get<ResponseSuccess>('/inbox-analyzer/subscription');

export const purchaseSubscription = (payload: PurchaseSubscriptionRequest) =>
  api.post<ResponseSuccess>('/subscription', payload);

export const purchaseLeadFinderSubscription = (
  payload: PurchaseSubscriptionRequest,
) => api.post<ResponseSuccess>('/lead-finder/subscription', payload);

export const purchaseInboxAnalyzerSubscription = (
  payload: PurchaseSubscriptionRequest,
) => api.post<ResponseSuccess>('/inbox-analyzer/subscription', payload);

export const getPlans = () => api.get<ResponseSuccess>('/plans');

export const getLeadFinderPlans = () =>
  api.get<ResponseSuccess>('/lead-finder/plans');

export const getInboxAnalyzerPlans = () =>
  api.get<ResponseSuccess>('/inbox-analyzer/subscription/plans');

export const getInvoiceUrl = (payload: GetInvoiceURLRequest) =>
  api.post<ResponseSuccess>('/invoice-url', payload);

export const getInboxAnalyzerInvoiceUrl = (payload: GetInvoiceURLRequest) =>
  api.post<ResponseSuccess>(
    '/inbox-analyzer/subscription/invoice-url',
    payload,
  );

export const getConnectedUsersAndEmailAccounts = () =>
  api.get<ResponseSuccess>('/accounts/connected-emails-and-users-count');

export const applyCouponCode = (payload: ApplyCouponCodeRequest) =>
  api.get<ResponseSuccess>(
    `/coupons/${payload.couponCode}/validate?planId=${payload.planId}&amount=${payload.amount}`,
  );

export const getEmailVerificationCredits = () =>
  api.get<ResponseSuccess>('/email-verification-credits');

export const getEmailVerificationPlan = () =>
  api.get<ResponseSuccess>('/email-verification-plans');

export const purchaseEmailVerificationCredits = (
  payload: PurchaseEmailVerificationCreditsRequest,
) => api.post<ResponseSuccess>('/email-verification-credits/purchase', payload);

export const modifySubscription = (payload: ModifySubscriptionRequest) =>
  api.get<ResponseSuccess>('/subscription/calculate-pay', {
    params: {
      accountSubscriptionPlanId: payload.accountSubscriptionPlanId,
      slots: payload.slots,
      stripeCardToken: payload.stripeCardToken,
    },
  });

export const modifyInboxAnalyzerSubscription = (
  payload: ModifySubscriptionRequest,
) =>
  api.get<ResponseSuccess>('/inbox-analyzer/subscription/calculate-pay', {
    params: {
      accountSubscriptionPlanId: payload.accountSubscriptionPlanId,
      slots: payload.slots,
      stripeCardToken: payload.stripeCardToken,
    },
  });

export const purchaseModifySubscription = (
  payload: PurchaseModifySubscriptionRequest,
) => api.post<ResponseSuccess>('/subscription/modify', payload);

export const purchaseLeadFinderModifySubscription = (
  payload: PurchaseModifySubscriptionRequest,
) => api.post<ResponseSuccess>('/lead-finder/subscription/modify', payload);

export const purchaseInboxAnalyzerModifySubscription = (
  payload: PurchaseModifySubscriptionRequest,
) => api.post<ResponseSuccess>('/inbox-analyzer/subscription/modify', payload);

export const reactivateSubscription = (
  payload: ReactivateResumeSubscriptionRequest,
) => api.post<ResponseSuccess>('/subscription/reactivate', payload);

export const reactivateLeadFinderSubscription = (
  payload: ReactivateResumeSubscriptionRequest,
) => api.post<ResponseSuccess>('/lead-finder/subscription/reactivate', payload);

export const reactivateInboxAnalyzerSubscription = (
  payload: ReactivateResumeSubscriptionRequest,
) =>
  api.post<ResponseSuccess>('/inbox-analyzer/subscription/reactivate', payload);

export const resumeSubscription = (
  payload: ReactivateResumeSubscriptionRequest,
) => api.post<ResponseSuccess>('/subscription/resume', payload);

export const resumeLeadFinderSubscription = (
  payload: ReactivateResumeSubscriptionRequest,
) => api.post<ResponseSuccess>('/lead-finder/subscription/resume', payload);

export const resumeInboxAnalyzerSubscription = (
  payload: ReactivateResumeSubscriptionRequest,
) => api.post<ResponseSuccess>('/inbox-analyzer/subscription/resume', payload);

export const handleSubscription = (payload: HandleSubscriptionRequest) =>
  api.post<ResponseSuccess>('/subscription/churnkey-event', payload);

export const handleLeadFinderChurnkeySubscription = (
  payload: HandleSubscriptionRequest,
) =>
  api.post<ResponseSuccess>(
    '/lead-finder/subscription/churnkey-event',
    payload,
  );

export const handleInboxAnalyzerChurnkeySubscription = (
  payload: HandleSubscriptionRequest,
) =>
  api.post<ResponseSuccess>(
    '/inbox-analyzer/subscription/churnkey-event',
    payload,
  );

export const calculatePay = (payload: CalculatePayRequest) =>
  api.get<ResponseSuccess>('/subscription/calculate-pay', { params: payload });

export const calculateInboxAnalyzerPay = (payload: CalculatePayRequest) =>
  api.get<ResponseSuccess>('/inbox-analyzer/subscription/calculate-pay', {
    params: payload,
  });

export const upgradePlan = (payload: UpgradePlanRequest) =>
  api.post<ResponseSuccess>('/subscriptions/upgrade', payload);

export const downgradePlan = (payload: DowngradePlanRequest) =>
  api.post<ResponseSuccess>('/subscriptions/downgrade', payload);

export const purchaseLtdPlan = (payload: LTDPurchasePayload) =>
  api.post<ResponseSuccess>('/ltd-purchase', payload);

export const upgradeLtdPlan = (payload: LTDUpgradePayload) =>
  api.patch<ResponseSuccess>('/ltd-upgrade ', payload);

export const modifyLtdPlan = (payload: LTDModifyPayload) =>
  api.patch<ResponseSuccess>('/ltd-modify  ', payload);

export const purchaseHistory = () =>
  api.get<ResponseSuccess>('/domain/purchase-history');

export const purchaseDetailsById = (payload: GetOrderDetailsByIdPayload) =>
  api.get<ResponseSuccess>(`/domain/purchase-details/${payload.orderId}`);

export const aiCreditHistory = (payload: ExportCreditHistoryPayload) =>
  api.post<ResponseSuccess>(`/ai-credits/export`, payload);

export const exportCreditHistory = (payload: ExportCreditHistoryPayload) =>
  api.post<ResponseSuccess>(`/credit-history/export`, payload);

export const getLeadFinderOneTimePlans = () =>
  api.get<ResponseSuccess>('/lead-finder/one-time-plans');

export const purchaseLeadFinderOneTimePlan = (
  payload: PurchaseLeadFinderCreditsRequest,
) => api.post<ResponseSuccess>('/lead-finder/credits', payload);

export const dynamicCustomPlans = (payload: DynamicCustomPlansRequest) =>
  api.post<ResponseSuccess>('/dynamic-plan-instantiation', payload);

/**
 * Calculate Ai Credits Pricings
 * @param payload - The payload for the request, Send all credits counts and type and currency code
 * @returns The response from the API
 */
export const calculateAiCreditsPricings = (
  payload: CalculateAiCreditsPricingsRequest,
) =>
  api.post<ResponseSuccess>(
    '/credit-exchange/calculate-total-credits',
    payload,
  );

/**
 * Purchase Ai Credits
 * @param payload - The payload for the request, Send the credit count, success url, cancel url and coupon code (optional)
 * @returns The response from the API
 */
export const purchaseAiCredits = (payload: PurchaseAiCreditsRequest) =>
  api.post<ResponseSuccess>('/credit-exchange/purchase-credits', payload);

/**
 * Get Default Credit Rate
 * @returns The response from the API
 */
export const getDefaultAiCreditRate = () =>
  api.get<ResponseSuccess>(
    'credit-exchange/get-default-credit-rate/AI_CREDITS',
  );

export const schedulePlanUpgrade = () =>
  api.post<ResponseSuccess>('/scheduled-plan-recurring');

export const calculateCreditsPrices = (
  payload: CalculateCreditsPricesRequest,
) =>
  api.post<ResponseSuccess>(
    '/credit-exchange/calculate-total-credits',
    payload,
  );

export const purchaseCredits = (payload: PurchaseCreditsRequest) =>
  api.post<ResponseSuccess>('/credit-exchange/purchase-credits', payload);

export const getCreditsTransactionStatus = (
  payload: CreditsTransactionStatusRequest,
) =>
  api.post<ResponseSuccess>(
    '/credit-exchange/polling/transaction-status',
    payload,
  );

// ─────────────────────────────────────────────────────────────────────────────
// PADDLE API FUNCTIONS — do not modify anything above this line
// ─────────────────────────────────────────────────────────────────────────────

// Utility
export const getPaddlePrefix = (provider?: string): string =>
  provider === 'paddle' ? '/paddle' : '';

// ── Subscription ──────────────────────────────────────────────────────────────
export const getPaddleSubscription = (application: PaddleApplicationType) =>
  api.get('/paddle/subscription', {
    params: { application },
  });

export const getPaddleSubscriptionMetadata = () =>
  api.get<PaddleSubscriptionMetadataItem[]>(
    `/paddle/subscription/metadata`
  );

// ── Plans ─────────────────────────────────────────────────────────────────────
export const getPaddlePlans = (type: PaddleApplicationType) =>
  api.get(`/paddle/plans`, { params: { type } });

// ── Upgrade Flow ──────────────────────────────────────────────────────────────
export const previewPaddleSubscription = (
  payload: PaddlePreviewSubscriptionRequest
) =>
  api.post<PaddlePreviewSubscriptionResponse>(
    `/paddle/preview-subscription`,
    payload
  );

export const upgradePaddleSubscription = (
  payload: PaddleUpgradeSubscriptionRequest
) =>
  api.post(`/paddle/upgrade-subscription`, payload);

export const pollPaddleTransactionStatus = (
  payload: PaddleTransactionPollingRequest
) =>
  api.post<PaddleTransactionPollingResponse>(
    `/paddle/polling/transaction-status`,
    payload
  );

export const changePaddleSubscriptionStatus = (
  payload: PaddleTransactionPollingRequest,
) =>
  api.post(
    `/paddle/update-account-subscription-status`,
    payload,
  );

// ── Dynamic Plan ──────────────────────────────────────────────────────────────
export const createPaddleDynamicPlan = (payload: PaddleDynamicPlanRequest) =>
  api.post(`/paddle/dynamic-plan-instantiation`, payload);

// ── Coupon ────────────────────────────────────────────────────────────────────
export const validatePaddleCoupon = (payload: PaddleValidateCouponRequest) =>
  api.post<PaddleValidateCouponResponse>(
    `/paddle/validate-coupon`,
    payload
  );

// ── Customer Portal ───────────────────────────────────────────────────────────
export const createPaddleCustomerPortalSession = (
  payload: PaddleCustomerPortalRequest
) =>
  api.post<PaddleCustomerPortalResponse>(
    `/paddle/customer-portal-session`,
    payload
  );

// ── Pause / Resume / Reactivate ───────────────────────────────────────────────
export const revokePaddleScheduledPause = (payload: PaddlePauseResumeRequest) =>
  api.patch(`/paddle/revoke-scheduled-pause`, payload);

export const resumePaddleSubscription = (payload: PaddlePauseResumeRequest) =>
  api.patch(`/paddle/resume`, payload);

export const reactivatePaddleSubscription = (payload: PaddlePauseResumeRequest) =>
  api.patch(`/paddle/reactivate`, payload);

// ── Customer Auth Token ──────────────────────────────────────────────────────
export const getCustomerAuthToken = (customerId: string) =>
  api.post(`/paddle/customer-auth-token`, { customerId });

// ── Payment Method Update ────────────────────────────────────────────────────
export const getUpdatePaymentMethodTransaction = (subscriptionId: string) =>
  api.post(`/paddle/update-payment-method-transaction`, { subscriptionId });

// ── Retain (Cancellation Flow) ──────────────────────────────────────────────
export const handlePaddleRetainEvent = (payload: PaddleRetainEventRequest) =>
  api.post('/paddle/retain-event', payload);

// ── Lead Finder Cancel Add-On ────────────────────────────────────────────────
export const cancelPaddleLeadFinderAddon = (payload: { accountSubscriptionPlanId: number }) =>
  api.post(`/paddle/lf-cancel-addon`, payload);

export const getDialerPhoneNumberPurchaseHistory = (params: {
  pageNumber: number;
  pageSize: number;
}) =>
  api.get<ResponseSuccess>('/dialer/phone-numbers/purchase-history', {
    params,
  });

export const reactivateDialerPhoneNumberSubscription = (payload: {
  paddleSubscriptionId: string;
}) =>
  api.patch<ResponseSuccess>(
    '/dialer/phone-numbers/purchase-history/reactivate',
    payload,
  );

export const exportDialerCreditsUsage = (payload: {
  startDate: string;
  endDate: string;
}) => api.post<ResponseSuccess>('/dialer/credits/export', payload);
